import { Injectable } from "@angular/core";

import { Observable } from "rxjs";

import { PaginatedResponse, QueryParams } from "@shared/models/query-params.model";
import { User, UserRole, UserType } from "@shared/models/user.model";
import { BaseHttpService } from "@shared/services/base-http.service";

export interface CreateUserDto {
  email?: string;
  username?: string | null;
  organizationId: string;
  role: UserRole;
  userType?: UserType;
}

export interface UpdateUserDto extends Partial<CreateUserDto> {
  id: string;
}

@Injectable({
  providedIn: "root"
})
export class UserHttpService extends BaseHttpService {
  protected override baseEndpoint = "admin/users";

  getById(userId: string): Observable<User> {
    return this.get<User>({
      endpoint: userId,
      type: User
    });
  }

  getAll(params?: QueryParams): Observable<PaginatedResponse<User>> {
    return this.getMany({
      queryParams: params,
      type: User
    });
  }

  create(data: CreateUserDto): Observable<{ user: User; secret?: string }> {
    return this.post<{ user: User; secret?: string }>({
      body: data
    });
  }

  update(data: UpdateUserDto): Observable<User> {
    return this.patch<User>({
      endpoint: `${data.id}`,
      body: data,
      type: User
    });
  }

  deleteUser(userId: string): Observable<void> {
    return this.delete<void>({
      endpoint: userId
    });
  }

  suspend(userId: string): Observable<User> {
    return this.patch<User>({
      endpoint: `${userId}/suspend`
    });
  }

  unsuspend(userId: string): Observable<User> {
    return this.patch<User>({
      endpoint: `${userId}/unsuspend`
    });
  }

  resendInvitation(userId: string): Observable<void> {
    return this.post<void>({
      endpoint: `${userId}/resend-invitation`
    });
  }

  checkEmailExists(email: string): Observable<{ exists: boolean; message?: string }> {
    return this.get<{ exists: boolean; message?: string }>({
      endpoint: `check-email`,
      options: {
        params: { email }
      }
    });
  }

  checkUsernameExists(username: string): Observable<{ exists: boolean; message?: string }> {
    return this.get<{ exists: boolean; message?: string }>({
      endpoint: `check-username`,
      options: {
        params: { username }
      }
    });
  }

  searchUsers(query: string): Observable<User[]> {
    return this.getManyWithoutPagination<User>({
      endpoint: `users/search`,
      skipBaseUrl: true,
      type: User,
      options: {
        params: { q: query }
      }
    });
  }

  // API Client specific methods
  regenerateApiClientSecret(userId: string): Observable<{ secret: string }> {
    return this.post<{ secret: string }>({
      endpoint: `${userId}/regenerate-secret`
    });
  }
}
